import { CenteredContainer } from '@nerdwallet/currency';
import { Fragment } from 'react';
import { BaseFixedFirstColumnTableProps } from './types';
import { getCellAlignment, getStyleClass, getTransformedData } from './utils';

import styles from './fixed-first-column-table.module.scss';

const BaseFixedFirstColumnTable = ({
  columns = [],
  tableData = [],
  tableStyle = '',
  centeredInParent = false,
  className = '',
  enableHighlighting = false,
  hideHeader = false,
  renderCell = undefined, // Custom cell renderer function
  rowKeyField = 'id',
  striped = false,
  transposed = false,
}: BaseFixedFirstColumnTableProps) => {
  if (!tableData || tableData.length === 0) {
    return (
      <div
        className={`${styles.emptyState} ${getStyleClass(
          tableStyle
        )} ${className}`}
      >
        <p>No data available</p>
      </div>
    );
  }

  if (!columns || columns.length === 0) {
    return (
      <div
        className={`${styles.emptyState} ${getStyleClass(
          tableStyle
        )} ${className}`}
      >
        <p>No columns defined</p>
      </div>
    );
  }

  // Transform the data and columns to the format expected by the table (Default or Transposed)
  const { transformedData, transformedColumns } = getTransformedData({
    transposed,
    data: tableData,
    columns,
  });

  // For transposed mode, track which original rows were highlighted
  const highlightedColumnIndices = new Set<number>();
  if (transposed && tableData) {
    tableData.forEach((row, index) => {
      if (row.highlight) {
        // In transposed mode, original rows become columns
        // Column 0 is for headers, so data[index] becomes column index + 1
        highlightedColumnIndices.add(index + 1);
      }
    });
  }

  const tableClasses = [
    styles.fixedTable,
    getStyleClass(tableStyle),
    striped ? styles.striped : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const TableContainer = centeredInParent ? CenteredContainer : Fragment;

  return (
    <div className={tableClasses}>
      <TableContainer>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            {!hideHeader && (
              <thead className={`${styles.thead}`}>
                <tr>
                  {transformedColumns.map((column, index) => (
                    <th
                      key={column.key}
                      className={`
                      ${styles.th}
                      ${index === 0 ? styles.firstColumn : ''}
                      ${getCellAlignment(column.align)}
                      ${column.headerClassName || ''}
                      ${
                        enableHighlighting &&
                        transposed &&
                        highlightedColumnIndices.has(index)
                          ? styles.highlightedColumn
                          : ''
                      }
                    `}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody className={`${styles.tbody}`}>
              {transformedData.map((row, rowIndex) => {
                const rowKey =
                  rowKeyField && row[rowKeyField]
                    ? `${rowKeyField}-${row[rowKeyField]}`
                    : `row-${JSON.stringify(row)
                        .slice(0, 50)
                        .replace(/[^a-zA-Z0-9]/g, '')}-${rowIndex}`;

                return (
                  <tr
                    key={rowKey}
                    className={`
                    ${styles.tr}
                    ${
                      enableHighlighting && !transposed && row.highlight
                        ? styles.highlighted
                        : ''
                    }
                  `}
                  >
                    {transformedColumns.map((column, colIndex) => (
                      <td
                        key={column.key}
                        className={`
                      ${styles.td}
                      ${colIndex === 0 ? styles.firstColumn : ''}
                      ${getCellAlignment(column.align)}
                      ${column.className || ''}
                      ${
                        enableHighlighting &&
                        transposed &&
                        highlightedColumnIndices.has(colIndex)
                          ? styles.highlightedColumn
                          : ''
                      }
                    `}
                      >
                        {renderCell
                          ? renderCell(column, row)
                          : row[column.key].text}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </TableContainer>
    </div>
  );
};

export default BaseFixedFirstColumnTable;
